import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Special } from '@common/entities';
import { updateLeasingJourneyErrors } from 'state/slices/navigationSlice';
import { RootState } from 'state/store';

import { AddonOption } from 'types/addon';
import { buildPageRequestData, validatePropertyId } from 'utils/buildPageRequest';
import {
  excludeHiddenGuarantorErrors,
  excludeHiddenGuarantorResponse,
} from 'utils/display-logic/hiddenGuarantorResponses';
import { getErrorCount } from 'utils/getErrors';
import handleError from 'utils/handleError';
import useRefreshScheduledCharges from 'utils/useRefreshScheduledCharges';

import { useIsCountBasedGuarantorHidden } from '../display-logic/useIsCountBasedGuarantorHidden';
import { useGetPageData } from '../useGetPageData';
import { useSaveResponseSynchronously } from '../useSaveResponseSynchronously';
import {
  getSecondaryApplicantSaveErrors,
  useSyncSecondaryApplicantsForPageSave,
} from '../useSyncSecondaryApplicantsForPageSave';

interface Props {
  activeStepIndex: number;
}

/**
 * Persists options data with PATCH `awaitSync=true`, then refreshes scheduled charges / cart.
 * Use for addon flows where the UI should match server pricing after `response.updated` side effects.
 */
export function useSaveOptionsPageSynchronously({ activeStepIndex }: Props) {
  const { formState, getFieldState } = useFormContext();
  const { errors } = formState;

  const dispatch = useDispatch();
  const { form, activeResponse, applicant } = useSelector((state: RootState) => state.application);
  const { pages } = form;
  const { applicationId, leaseId } = activeResponse;
  const { propertyId } = applicant;

  const { patchSynchronously, isSyncing } = useSaveResponseSynchronously();
  const getPageData = useGetPageData();
  const syncSecondaryApplicantsForPageSave = useSyncSecondaryApplicantsForPageSave();
  const [isSaving, setIsSaving] = useState(false);
  const isGuarantorHidden = useIsCountBasedGuarantorHidden(pages[activeStepIndex]?.id);

  const { refreshScheduledCharges } = useRefreshScheduledCharges({
    propertyId,
    leaseId,
  });

  async function saveOptionsPageSynchronously(
    newPageIndex: number,
    addOns: AddonOption[],
    selectedSpecials?: Special[],
  ) {
    if (!validatePropertyId(propertyId)) throw new Error('Property ID not available');
    setIsSaving(true);
    try {
      const pageResponses = await syncSecondaryApplicantsForPageSave(
        excludeHiddenGuarantorResponse(getPageData(activeStepIndex), isGuarantorHidden),
        pages[activeStepIndex].id,
      );
      const errorCount = getErrorCount(
        excludeHiddenGuarantorErrors(
          getSecondaryApplicantSaveErrors(errors, getFieldState),
          isGuarantorHidden,
        ),
      );
      const { requestData } = buildPageRequestData({
        activeStepIndex,
        newPageIndex,
        pages,
        applicationId,
        propertyId,
        getPageResponses: () => pageResponses,
        errorCount,
        addOns,
        selectedSpecials,
      });

      await patchSynchronously(requestData);
      await refreshScheduledCharges();
      dispatch(
        updateLeasingJourneyErrors({
          pageIndex: activeStepIndex,
          count: errorCount,
        }),
      );
    } catch (error) {
      handleError(`Form Viewer - Error saving options: ${error}`, `Error saving options`);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }

  return { saveOptionsPageSynchronously, isLoading: isSyncing || isSaving };
}
